import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { reminderPreferencesAPI } from '../services/api';

type Props = {
  initialEmail: string | null;
  onSaved?: (email: string) => void;
};

const ScormEmailSetup: React.FC<Props> = ({ initialEmail, onSaved }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [emailConfirm, setEmailConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (initialEmail) {
    return null;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(false);

    if (email.trim().toLowerCase() !== emailConfirm.trim().toLowerCase()) {
      setError(t('reminderEmails.emailMismatch'));
      return;
    }

    setSaving(true);
    try {
      const result = await reminderPreferencesAPI.setScormEmail(email, emailConfirm);
      setSuccess(true);
      if (result.email && onSaved) {
        onSaved(result.email);
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        t('reminderEmails.saveError');
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} className="reminder-emails__scorm-setup">
      <Typography variant="subtitle1" className="reminder-emails__title">
        {t('reminderEmails.scormEmailTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('reminderEmails.scormEmailDescription')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('reminderEmails.scormEmailSaved')}
        </Alert>
      )}

      <TextField
        fullWidth
        type="email"
        label={t('reminderEmails.emailLabel')}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        sx={{ mb: 2 }}
      />
      <TextField
        fullWidth
        type="email"
        label={t('reminderEmails.emailConfirmLabel')}
        value={emailConfirm}
        onChange={(e) => setEmailConfirm(e.target.value)}
        required
        sx={{ mb: 2 }}
      />

      <Button type="submit" variant="contained" disabled={saving}>
        {saving ? <CircularProgress size={22} color="inherit" /> : t('reminderEmails.saveEmail')}
      </Button>
    </Box>
  );
};

export default ScormEmailSetup;
